/**
 * 존이 **실제로 쓰는 그림** 목록 — 맵에서 읽어낸다(FR-ADMIN / FR-WORLD).
 *
 * 왜 필요한가: 관리자의 그림 목록은 "올릴 수 있는 자리(슬롯)" 기준이라, 운영자가
 * "경주존 그림 바꾸자" 할 때 **그 존에 무엇이 쓰이는지** 알 방법이 없었다(외우거나 맵 JSON 을 봐야 했다).
 * 여기서 맵을 훑어 쓰이는 슬롯만 뽑아내면, 새 오브젝트를 배치하는 순간 목록에 자동으로 생긴다
 * (하드코딩 금지 — 콘텐츠 주도).
 */

/** 그림 한 칸 — 관리자 화면의 업로드 카드 하나에 대응. */
export interface ZoneImageRef {
  slotKey: string; // 매니페스트 키(예: world.background.gyeongju, npc.greg_white)
  label: string; // 운영자가 읽을 말(예: "첨성대 눌렀을 때 그림")
  group: ZoneImageGroup;
  fromObject?: string; // 어느 오브젝트에서 왔는지(중복 표시·추적용)
}
export type ZoneImageGroup = "bg" | "npc" | "card" | "decor" | "object";

export const ZONE_IMAGE_GROUP_LABEL: Record<ZoneImageGroup, string> = {
  bg: "바탕 (배경·바닥·벽)",
  npc: "이 존에 서 있는 친구들",
  card: "눌렀을 때 나오는 그림",
  decor: "장식 (건물·소품)",
  object: "그 밖의 그림",
};

/** 존마다 항상 있는 바탕 3종 — 맵에 안 적혀 있어도 웹이 이 키를 찾는다. */
const BASE_SLOTS: { suffix: string; label: string }[] = [
  { suffix: "world.background", label: "배경 일러스트" },
  { suffix: "world.tileset", label: "바닥 타일" },
  { suffix: "world.wall", label: "벽 타일" },
];

interface TiledProp {
  name?: unknown;
  value?: unknown;
}
interface TiledObject {
  name?: unknown;
  properties?: unknown;
}
interface TiledLayer {
  objects?: unknown;
}

function propsOf(o: TiledObject): Record<string, string> {
  const list = Array.isArray(o.properties) ? (o.properties as TiledProp[]) : [];
  const out: Record<string, string> = {};
  for (const p of list) {
    if (typeof p?.name === "string" && typeof p.value === "string") out[p.name] = p.value;
  }
  return out;
}

function objectsOf(map: unknown): Record<string, string>[] {
  const layers = Array.isArray((map as { layers?: unknown })?.layers)
    ? ((map as { layers: TiledLayer[] }).layers)
    : [];
  const out: Record<string, string>[] = [];
  for (const l of layers) {
    if (!Array.isArray(l?.objects)) continue;
    for (const o of l.objects as TiledObject[]) {
      if (o && typeof o === "object") out.push(propsOf(o));
    }
  }
  return out;
}

/**
 * 맵에서 쓰이는 그림 칸 목록. 같은 슬롯이 여러 번 쓰이면 **한 번만** 낸다
 * (그레그가 세 존에 서 있어도 그림은 하나다 — 두 번 올리게 하면 헷갈린다).
 */
export function collectZoneImages(zone: string, map: unknown): ZoneImageRef[] {
  const out: ZoneImageRef[] = [];
  const seen = new Set<string>();
  const push = (r: ZoneImageRef) => {
    if (!r.slotKey || seen.has(r.slotKey)) return;
    seen.add(r.slotKey);
    out.push(r);
  };

  for (const b of BASE_SLOTS) push({ slotKey: `${b.suffix}.${zone}`, label: b.label, group: "bg" });

  for (const p of objectsOf(map)) {
    const title = p.title_ko || "이름 없음";
    if (p.npc_sprite) {
      push({ slotKey: `npc.${p.npc_sprite}`, label: title, group: "npc", fromObject: title });
    }
    if (p.decor_sprite) {
      push({ slotKey: `decor.${p.decor_sprite}`, label: title, group: "decor", fromObject: title });
    }
    // card_image·sprite 는 매니페스트 키를 **그대로** 가리킨다(접두사 붙이지 않는다).
    if (p.card_image) {
      push({ slotKey: p.card_image, label: `${title} 눌렀을 때 그림`, group: "card", fromObject: title });
    }
    if (p.sprite) {
      push({ slotKey: p.sprite, label: `${title} 표지 그림`, group: "object", fromObject: title });
    }
  }
  return out;
}

/** 화면에 묶어 보여주기 좋게 그룹별로 나눈다(빈 그룹은 생략). */
export function groupZoneImages(refs: ZoneImageRef[]): { group: ZoneImageGroup; label: string; items: ZoneImageRef[] }[] {
  const order: ZoneImageGroup[] = ["bg", "npc", "card", "decor", "object"];
  return order
    .map((g) => ({ group: g, label: ZONE_IMAGE_GROUP_LABEL[g], items: refs.filter((r) => r.group === g) }))
    .filter((s) => s.items.length > 0);
}
